import { Sparkles, TrendingUp } from "lucide-react";
import { trialDaysLeft, usePremium } from "../lib/premium";
import { useAiUsage } from "../lib/usage";
import { BILLING_CONFIG, formatUsd } from "../lib/billingConfig";

/**
 * Settings-panel widget showing the user's AI credit usage for the
 * current month. Renders nothing when the user is signed out or
 * Firebase isn't configured — there's no usage to show.
 *
 * Two tiers, two layouts:
 *
 *   - **Free** ({@link BILLING_CONFIG.freeMonthlyCredits} credits/mo):
 *     a small progress bar with "X of Y free this month" and an
 *     "Upgrade for more credits" button that triggers the upsell overlay.
 *   - **Premium** ({@link BILLING_CONFIG.premiumIncludedCredits}
 *     credits/mo included): full progress bar with the included usage,
 *     plus an overage section that appears once the user crosses the
 *     limit (informational — auto-overage is enabled, so the next
 *     invoice will reflect the extra usage).
 *
 * Period boundary is UTC month — same as the server. The "Resets
 * monthly" hint sidesteps any expectation of wall-clock-local rollover.
 */
export function UsageMeter({
  onUpgradeClick,
}: {
  /** Optional. Wired to the upsell overlay in Settings. Omit to hide
   *  the upgrade CTA (e.g. on a Premium user's page where the meter is
   *  informational only). */
  onUpgradeClick?: () => void;
}) {
  const usage = useAiUsage();
  const premium = usePremium();
  const daysLeft = trialDaysLeft(premium);

  if (usage.status === "signed-out" || usage.status === "unavailable") {
    return null;
  }

  // The first paint after sign-in lands in "loading" — render a skeleton
  // so the meter doesn't pop in. Identical layout sizes so the section
  // doesn't reflow when the snapshot resolves.
  if (usage.status === "loading") {
    return (
      <div
        className="border-t border-ink-100 pt-4"
        data-testid="usage-meter-loading"
      >
        <div className="text-xs font-medium text-ink-500 mb-2">
          Credits this month
        </div>
        <div className="p-3 rounded-xl bg-ink-50 ring-1 ring-ink-100">
          <div className="h-4 w-32 bg-ink-100 rounded animate-pulse" />
          <div className="mt-3 h-2 w-full bg-ink-100 rounded-full animate-pulse" />
        </div>
      </div>
    );
  }

  const { used, included, plan, overageCount, projectedOverageCostUsd } = usage;
  // Clamp progress to the included quota; overage gets its own bar.
  const inIncluded = Math.min(used, included);
  const pct = Math.min(100, Math.round((inIncluded / included) * 100));
  const remaining = Math.max(0, included - used);
  // Slight visual emphasis when nearing the cap — feel free to tweak
  // the threshold; 80% covers the common "almost out" case.
  const nearLimit = pct >= 80;

  return (
    <div
      className="border-t border-ink-100 pt-4"
      data-testid="usage-meter"
      data-plan={plan}
    >
      <div className="text-xs font-medium text-ink-500 mb-2">
        Credits this month
      </div>
      <div className="p-3 rounded-xl bg-ink-50 ring-1 ring-ink-100">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-body-sm font-semibold text-ink-900 flex items-center gap-1.5">
              <Sparkles className="size-3.5 text-violet-600" />
              {plan === "premium" ? (premium.trial ? "Premium trial" : "Premium") : "Free"}
              {premium.trial && daysLeft != null && (
                <span
                  className="rounded-full bg-violet-50 px-1.5 py-0.5 text-[0.65rem] font-semibold text-violet-700 ring-1 ring-violet-200"
                  data-testid="usage-meter-trial"
                >
                  {daysLeft === 0 ? "ends today" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
                </span>
              )}
            </div>
            <div className="text-caption text-ink-500 mt-0.5">
              {plan === "premium"
                ? `${used.toLocaleString()} of ${included.toLocaleString()} credits used`
                : `${used} of ${included} credits used`}
            </div>
          </div>
          <div
            className="text-caption text-ink-500 tabular-nums shrink-0"
            data-testid="usage-meter-remaining"
          >
            {remaining > 0
              ? `${remaining} left`
              : plan === "premium"
                ? "Included used up"
                : "Limit reached"}
          </div>
        </div>

        <div className="mt-3 h-2 w-full bg-ink-200/70 rounded-full overflow-hidden">
          <div
            className={
              nearLimit
                ? "h-full bg-amber-500 transition-all"
                : "h-full bg-violet-600 transition-all"
            }
            style={{ width: `${pct}%` }}
            data-testid="usage-meter-bar"
          />
        </div>

        {plan === "premium" && overageCount > 0 && (
          <div
            className="mt-3 flex items-start gap-2 p-2.5 rounded-lg bg-white ring-1 ring-amber-200"
            data-testid="usage-meter-overage"
          >
            <TrendingUp className="size-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-caption text-ink-700 leading-snug">
              <span className="font-semibold text-ink-900">
                +{overageCount.toLocaleString()} extra
              </span>{" "}
              at {formatUsd(BILLING_CONFIG.overagePriceUsdPerCredit)} each ={" "}
              <span
                className="font-semibold text-ink-900 tabular-nums"
                data-testid="usage-meter-overage-cost"
              >
                {formatUsd(projectedOverageCostUsd)}
              </span>{" "}
              on your next invoice.
            </div>
          </div>
        )}

        <div className="mt-2 text-caption text-ink-400">
          Resets monthly. A Feng Shui layout, a room score, and a product
          import each use 1 credit.
        </div>

        {plan === "free" && onUpgradeClick && (
          <button
            type="button"
            onClick={onUpgradeClick}
            data-testid="usage-meter-upgrade"
            className="mt-3 w-full h-9 rounded-lg bg-white ring-1 ring-ink-200 hover:bg-ink-100 active:bg-ink-200 text-body-sm font-semibold text-ink-900 inline-flex items-center justify-center gap-1.5"
          >
            <Sparkles className="size-3.5 text-violet-600" />
            Upgrade for more credits
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Compact inline budget hint shown on the AI sheets near the action
 * button. Two flavors:
 *
 *   - Free: "3 of 5 free this month" (turns amber at 80%).
 *   - Premium: shown only when the user is at or past their included
 *     quota — "You're past your included usage. Each generation is
 *     $0.50 on your next invoice."
 *
 * Returns null in any other state (loading, signed-out, premium with
 * headroom) so the AI sheet doesn't sprout an empty info block.
 */
export function UsageBudgetWarning() {
  const usage = useAiUsage();
  if (usage.status !== "ready") return null;

  if (usage.plan === "free") {
    const remaining = Math.max(0, usage.included - usage.used);
    const nearLimit = remaining <= Math.ceil(usage.included * 0.2);
    return (
      <div
        className={
          nearLimit
            ? "mt-3 p-2.5 rounded-lg bg-amber-50 ring-1 ring-amber-200 text-caption text-amber-900 flex items-center gap-2"
            : "mt-3 p-2.5 rounded-lg bg-ink-50 ring-1 ring-ink-100 text-caption text-ink-700 flex items-center gap-2"
        }
        data-testid="usage-budget-warning"
      >
        <Sparkles
          className={
            nearLimit
              ? "size-3.5 text-amber-600 shrink-0"
              : "size-3.5 text-violet-600 shrink-0"
          }
        />
        <span>
          {remaining > 0 ? (
            <>
              <span className="font-semibold tabular-nums">{remaining}</span>{" "}
              of {usage.included} free credits left this month.
            </>
          ) : (
            <>You've used all {usage.included} free credits this month.</>
          )}
        </span>
      </div>
    );
  }

  // Premium: only nag when they're already over.
  if (usage.atOrOverIncluded) {
    return (
      <div
        className="mt-3 p-2.5 rounded-lg bg-amber-50 ring-1 ring-amber-200 text-caption text-amber-900 flex items-start gap-2"
        data-testid="usage-budget-warning"
      >
        <TrendingUp className="size-3.5 text-amber-600 mt-0.5 shrink-0" />
        <span>
          You're past your{" "}
          <span className="font-semibold tabular-nums">{usage.included}</span>{" "}
          included credits this month. Each extra credit is{" "}
          {formatUsd(BILLING_CONFIG.overagePriceUsdPerCredit)} on your next
          invoice
          {usage.overageCount > 0 ? (
            <>
              {" "}
              (so far:{" "}
              <span
                className="font-semibold tabular-nums"
                data-testid="usage-budget-warning-overage"
              >
                {formatUsd(usage.projectedOverageCostUsd)}
              </span>
              ).
            </>
          ) : (
            <>.</>
          )}
        </span>
      </div>
    );
  }

  return null;
}
